<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Servicios a Domicilio</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Servicios Externos
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ servicios.length }}
          </span>
        </h1>
      </div>
      
      <button 
        @click="abrirModalCreacion"
        class="px-3 sm:px-6 py-2 sm:py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-[10px] sm:text-sm tracking-widest transition-all shadow-lg shadow-indigo-500/20 flex items-center gap-2 w-full sm:w-auto justify-center"
      >
        <Icon icon="mdi:plus-circle" class="text-base sm:text-xl" />
        NUEVO SERVICIO
      </button>
    </div>

    <!-- Filters -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-7 lg:col-span-8">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Búsqueda</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
            <input
              v-model="filtroBusqueda"
              type="text"
              placeholder="Buscar por cliente, descripción, dirección o técnico..."
              class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
            />
          </div>
        </div>
        
        <div class="sm:col-span-3 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select
            v-model="filtroEstado"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="">TODOS</option>
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="EN PROCESO">EN PROCESO</option>
            <option value="COMPLETADO">COMPLETADO</option>
            <option value="CANCELADO">CANCELADO</option>
          </select>
        </div>

        <div class="sm:col-span-2 lg:col-span-2">
          <button 
            @click="cargarServicios"
            class="w-full py-2.5 sm:py-3.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600 shadow-sm"
          >
            <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-lg sm:text-xl" />
            Refrescar
          </button>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <!-- Desktop -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Servicio</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Cliente</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Acciones</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Descripción</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Prioridad</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Fecha</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Total</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Técnico</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="9" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-if="!loading && serviciosFiltrados.length === 0">
              <td colspan="9" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:database-off-outline" class="text-7xl mb-3" />
                  <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay servicios registrados</h4>
                </div>
              </td>
            </tr>
            <tr v-for="item in paginatedServicios" :key="item.idServicioExterno" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all group">
              <td class="px-5 py-4">
                <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800">
                  #{{ item.numeroServicio }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs">
                    {{ (item.nombreCliente || '?').charAt(0) }}
                  </div>
                  <p class="font-black text-gray-900 dark:text-white text-xs">{{ item.nombreCliente || 'N/A' }}</p>
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-center gap-1 flex-nowrap whitespace-nowrap">
                  <button 
                    @click="abrirModalDetalles(item)" 
                    class="p-1.5 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-700 hover:text-white transition-all shadow-sm flex-shrink-0" 
                    title="Ver detalles">
                    <Icon icon="mdi:eye-outline" class="text-base" />
                  </button>
                  
                  <button 
                    v-if="item.estado !== 'COMPLETADO'"
                    @click="abrirModalEdicion(item)" 
                    class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm flex-shrink-0" 
                    title="Editar">
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>
                  
                  <button 
                    v-if="item.estado === 'EN PROCESO'"
                    @click="abrirModalItems(item)" 
                    class="p-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-600 hover:text-white transition-all shadow-sm flex-shrink-0" 
                    title="Agregar/Editar items">
                    <Icon icon="mdi:cart-plus" class="text-base" />
                  </button>
                  
                  <button 
                    v-if="item.estado === 'EN PROCESO' && (item.totalUsd > 0 || item.subtotalUsd > 0)"
                    @click="abrirModalPago(item)" 
                    class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex-shrink-0" 
                    title="Cobrar">
                    <Icon icon="mdi:cash-check" class="text-base" />
                  </button>
                  
                  <button @click="confirmarEliminar(item)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Eliminar servicio">
                    <Icon icon="mdi:trash-can-outline" class="text-base" />
                  </button>
                </div>
              </td>
              <td class="px-5 py-4">
                <p class="font-black text-gray-900 dark:text-white text-xs truncate max-w-[150px]" :title="item.descripcion">{{ item.descripcion || '-' }}</p>
              </td>
              <td class="px-5 py-4 text-center">
                <div :class="getPrioridadStyle(item.prioridad)" class="px-2.5 py-0.5 rounded-full text-[8px] font-black inline-block uppercase">
                  {{ item.prioridad || 'MEDIA' }}
                </div>
              </td>
              <td class="px-5 py-4 whitespace-nowrap">
                <div class="space-y-0.5">
                  <p class="font-black text-gray-900 dark:text-white text-xs">{{ formatearFecha(item.fechaProgramada) }}</p>
                  <p class="text-[9px] text-gray-400 font-black uppercase">{{ formatearHora(item.fechaProgramada) }}</p>
                </div>
              </td>
              <td class="px-5 py-4">
                <span v-if="item.totalUsd > 0" class="font-black text-emerald-600 dark:text-emerald-400 text-sm">
                  ${{ (item.totalUsd || 0).toFixed(2) }}
                </span>
                <span v-else class="text-gray-400 text-[10px] font-black uppercase">Sin asignar</span>
              </td>
              <td class="px-5 py-4 text-center">
                <div :class="getEstadoStyle(item.estado)" class="px-3 py-1 rounded-lg text-[10px] font-black uppercase border shadow-sm inline-block">
                  {{ item.estado || 'PENDIENTE' }}
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-2">
                  <Icon icon="mdi:account-wrench" class="text-xs text-purple-500" />
                  <span class="text-[10px] font-black text-gray-600 dark:text-gray-300 truncate">{{ item.nombreTecnico || 'Sin asignar' }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile -->
      <div class="lg:hidden space-y-3 p-2">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!loading && serviciosFiltrados.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:database-off-outline" class="text-6xl mb-3" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay servicios registrados</h4>
          </div>
        </div>
        
        <div v-for="item in paginatedServicios" :key="item.idServicioExterno" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
          
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800">
                #{{ item.numeroServicio }}
              </span>
              <div :class="getEstadoStyle(item.estado)" class="px-2 py-0.5 rounded-lg text-[8px] font-black uppercase border shadow-sm">
                {{ item.estado || 'PENDIENTE' }}
              </div>
            </div>
            <div :class="getPrioridadStyle(item.prioridad)" class="px-2 py-0.5 rounded-full text-[7px] font-black uppercase">
              {{ item.prioridad || 'MEDIA' }}
            </div>
          </div>

          <div class="flex items-center gap-2 mb-2">
            <div class="w-7 h-7 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs flex-shrink-0">
              {{ (item.nombreCliente || '?').charAt(0) }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-black text-gray-900 dark:text-white text-xs leading-tight truncate">{{ item.nombreCliente || 'N/A' }}</p>
              <p class="text-[8px] font-black text-gray-400 truncate">{{ item.descripcion || '-' }}</p>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-1 mb-2 text-[10px]">
            <div class="flex items-center gap-1">
              <Icon icon="mdi:calendar" class="text-gray-400 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400">{{ formatearFecha(item.fechaProgramada) }}</span>
            </div>
            <div class="flex items-center gap-1 justify-end">
              <span v-if="item.totalUsd > 0" class="font-black text-emerald-600 dark:text-emerald-400">${{ (item.totalUsd || 0).toFixed(2) }}</span>
              <span v-else class="text-gray-400 text-[9px] font-black uppercase">Sin asignar</span>
            </div>
          </div>

          <div class="flex items-center gap-2 mb-2 text-[9px] bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <Icon icon="mdi:account-wrench" class="text-purple-500 text-sm" />
            <span class="font-bold text-gray-600 dark:text-gray-400">{{ item.nombreTecnico || 'Sin asignar' }}</span>
          </div>

          <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button @click="abrirModalDetalles(item)" class="p-1.5 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-700 hover:text-white transition-all shadow-sm" title="Ver detalles">
              <Icon icon="mdi:eye-outline" class="text-sm" />
            </button>
            
            <button 
              v-if="item.estado !== 'COMPLETADO'"
              @click="abrirModalEdicion(item)" 
              class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm" 
              title="Editar">
              <Icon icon="mdi:pencil" class="text-sm" />
            </button>
            
            <button 
              v-if="item.estado === 'EN PROCESO'"
              @click="abrirModalItems(item)" 
              class="p-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-600 hover:text-white transition-all shadow-sm" 
              title="Agregar/Editar items">
              <Icon icon="mdi:cart-plus" class="text-sm" />
            </button>
            
            <button 
              v-if="item.estado === 'EN PROCESO' && (item.totalUsd > 0 || item.subtotalUsd > 0)"
              @click="abrirModalPago(item)" 
              class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm" 
              title="Cobrar">
              <Icon icon="mdi:cash-check" class="text-sm" />
            </button>
            
            <button @click="confirmarEliminar(item)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm" title="Eliminar">
              <Icon icon="mdi:trash-can-outline" class="text-sm" />
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="serviciosFiltrados.length > itemsPerPage" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest">
          MOSTRANDO {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, serviciosFiltrados.length) }} DE {{ serviciosFiltrados.length }}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="goToFirstPage" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-left" class="text-base sm:text-lg" />
          </button>
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
            <button v-for="page in displayedPages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>
          <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
          <button @click="goToLastPage" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-right" class="text-base sm:text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL FORMULARIO (Editar / Crear) -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showFormModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9998]" @click.self="showFormModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700 animate-zoomIn">
          <div class="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-indigo-600 to-violet-700 text-white flex justify-between items-center flex-shrink-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon :icon="editando ? 'mdi:pencil-box' : 'mdi:plus-box'" class="text-xl sm:text-2xl" />
              </div>
              <div>
                <h2 class="text-sm sm:text-lg font-black leading-none">{{ editando ? 'EDITAR SERVICIO' : 'NUEVO SERVICIO' }}</h2>
                <p class="text-[8px] sm:text-[10px] text-indigo-100 font-bold tracking-widest uppercase mt-0.5">Jean Informático</p>
              </div>
            </div>
            <button @click="showFormModal = false" class="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors">
              <Icon icon="mdi:close" class="text-xl sm:text-2xl" />
            </button>
          </div>

          <div v-if="editando" class="px-4 sm:px-6 py-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-b-2 border-amber-200 dark:border-amber-800 flex flex-wrap items-center gap-3 flex-shrink-0">
            <div class="flex items-center gap-2">
              <Icon icon="mdi:state-machine" class="text-amber-600 dark:text-amber-400 text-lg" />
              <span class="text-[10px] font-black uppercase text-amber-700 dark:text-amber-300 tracking-widest">Cambiar Estado:</span>
            </div>
            <div class="flex gap-1.5 flex-wrap">
              <button 
                v-for="est in ['PENDIENTE', 'EN PROCESO', 'CANCELADO']"
                :key="est"
                @click="form.estado = est"
                :class="[
                  'px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all border-2',
                  form.estado === est 
                    ? getEstadoStyle(est) + ' border-transparent scale-105'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-amber-400'
                ]"
              >
                {{ est }}
              </button>
            </div>
          </div>

          <div class="p-3 sm:p-6 overflow-y-auto custom-scrollbar bg-gray-50 dark:bg-gray-900/50 max-h-[calc(95vh-200px)] flex-1">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <div class="space-y-3 sm:space-y-4">
                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-visible">
                  <div class="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700 rounded-t-2xl">
                    <h2 class="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center">
                      <div class="p-1 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg mr-2">
                        <Icon icon="mdi:account" class="text-indigo-600 dark:text-indigo-400 text-base sm:text-lg" />
                      </div>
                      Información del Cliente
                    </h2>
                  </div>
                  <div class="p-3 sm:p-4">
                    <div v-if="!editando && !form.idCliente" class="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl mb-3 sm:mb-4">
                      <button @click="modoCliente = 'buscar'" :class="['flex-1 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-bold rounded-lg transition-all', modoCliente === 'buscar' ? 'bg-white dark:bg-gray-800 text-indigo-600 shadow-sm' : 'text-gray-400']">BUSCAR</button>
                      <button @click="modoCliente = 'crear'" :class="['flex-1 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-bold rounded-lg transition-all', modoCliente === 'crear' ? 'bg-white dark:bg-gray-800 text-indigo-600 shadow-sm' : 'text-gray-400']">NUEVO</button>
                    </div>

                    <div v-if="modoCliente === 'buscar' && !form.idCliente" class="relative">
                      <div class="relative">
                        <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base sm:text-lg" />
                        <input v-model="busquedaCliente" placeholder="Buscar por nombre o documento..." class="w-full pl-9 sm:pl-10 pr-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 text-xs sm:text-sm transition-all dark:text-white" />
                      </div>
                      <div v-if="busquedaCliente && clientesFiltrados.length" class="absolute z-[100] mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
                        <div v-for="c in clientesFiltrados" :key="c.idCliente" @click="seleccionarCliente(c)" class="p-2 sm:p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                          <p class="font-bold text-xs sm:text-sm text-gray-800 dark:text-white">{{ c.nombre }} {{ c.apellido }}</p>
                          <p class="text-[9px] sm:text-[10px] text-gray-500">{{ c.documento }} • {{ c.telefono }}</p>
                        </div>
                      </div>
                    </div>

                    <div v-if="modoCliente === 'crear' && !form.idCliente" class="space-y-2 sm:space-y-3 animate-fadeIn">
                      <div class="grid grid-cols-2 gap-2 sm:gap-3">
                        <input v-model="nuevoCliente.nombre" placeholder="Nombre *" class="px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                        <input v-model="nuevoCliente.apellido" placeholder="Apellido" class="px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                      </div>
                      <div class="grid grid-cols-2 gap-2 sm:gap-3">
                        <input v-model="nuevoCliente.documento" placeholder="Documento" class="px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                        <input v-model="nuevoCliente.telefono" placeholder="Teléfono *" class="px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                      </div>
                      <textarea v-model="nuevoCliente.direccion" placeholder="Dirección" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" rows="2"></textarea>
                      <button @click="crearYSeleccionarCliente" class="w-full py-2 bg-indigo-600 text-white rounded-xl text-[10px] sm:text-xs font-black hover:bg-indigo-700 transition-all flex items-center justify-center gap-2">
                        <Icon icon="mdi:account-plus" /> GUARDAR Y SELECCIONAR
                      </button>
                    </div>

                    <div v-if="form.idCliente" class="p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/10 dark:to-blue-900/10 rounded-2xl border border-indigo-200 dark:border-indigo-800/50 flex items-center justify-between">
                      <div class="flex items-center gap-2 sm:gap-3">
                        <div class="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
                          <Icon icon="mdi:account-check" class="text-indigo-600 dark:text-indigo-400 text-base sm:text-xl" />
                        </div>
                        <div>
                          <p class="font-bold text-xs sm:text-sm text-gray-800 dark:text-white">{{ clienteActual?.nombre }} {{ clienteActual?.apellido }}</p>
                          <p class="text-[9px] sm:text-[10px] text-gray-500 font-mono">{{ clienteActual?.documento }} • {{ clienteActual?.telefono }}</p>
                        </div>
                      </div>
                      <button v-if="!editando" @click="form.idCliente = null" class="p-1 text-gray-400 hover:text-red-500 transition-colors">
                        <Icon icon="mdi:close-circle" class="text-lg sm:text-xl" />
                      </button>
                    </div>
                  </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div class="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <h2 class="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center">
                      <div class="p-1 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg mr-2">
                        <Icon icon="mdi:toolbox" class="text-emerald-600 dark:text-emerald-400 text-base sm:text-lg" />
                      </div>
                      Detalles del Servicio
                    </h2>
                  </div>
                  <div class="p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Descripción del Servicio *</label>
                      <textarea v-model="form.descripcion" rows="2" placeholder="¿Qué se va a realizar?" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white"></textarea>
                    </div>
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Dirección *</label>
                      <textarea v-model="form.direccion" rows="2" placeholder="Lugar donde se realizará el servicio" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white"></textarea>
                    </div>
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Ubicación GPS (Link)</label>
                      <input v-model="form.ubicacionGps" placeholder="https://maps.google.com/..." class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="space-y-3 sm:space-y-4">
                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div class="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <h2 class="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center">
                      <div class="p-1 bg-orange-100 dark:bg-orange-900/30 rounded-lg mr-2">
                        <Icon icon="mdi:calendar-clock" class="text-orange-600 dark:text-orange-400 text-base sm:text-lg" />
                      </div>
                      Planificación
                    </h2>
                  </div>
                  <div class="p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">PRIORIDAD</label>
                      <select v-model="form.prioridad" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-[10px] sm:text-xs font-black dark:bg-gray-700 dark:text-white">
                        <option value="BAJA">🟢 BAJA</option>
                        <option value="MEDIA">🟡 MEDIA</option>
                        <option value="ALTA">🔴 ALTA</option>
                        <option value="URGENTE">🔥 URGENTE</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Fecha Programada</label>
                      <input v-model="form.fechaProgramada" type="datetime-local" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                    </div>
                  </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div class="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <h2 class="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center">
                      <div class="p-1 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-2">
                        <Icon icon="mdi:account-wrench" class="text-purple-600 dark:text-purple-400 text-base sm:text-lg" />
                      </div>
                      Técnico Asignado
                    </h2>
                  </div>
                  <div class="p-3 sm:p-4">
                    <button 
                      @click="abrirModalTecnicos"
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-500 transition-all group flex items-center justify-between"
                    >
                      <div class="flex items-center gap-3">
                        <div class="p-2 rounded-xl" :class="form.idTecnico ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-200 dark:bg-gray-700'">
                          <Icon 
                            :icon="form.idTecnico ? 'mdi:account-check' : 'mdi:account-plus'" 
                            :class="form.idTecnico ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'"
                            class="text-lg"
                          />
                        </div>
                        <div class="text-left">
                          <p class="font-bold text-sm text-gray-800 dark:text-white">
                            {{ nombreTecnicoAsignado || 'Seleccionar Técnico' }}
                          </p>
                          <p class="text-[9px] text-gray-400 font-medium">
                            {{ form.idTecnico ? 'Técnico asignado ✓' : 'Click para asignar' }}
                          </p>
                        </div>
                      </div>
                      <Icon icon="mdi:chevron-right" class="text-gray-400 group-hover:text-purple-500 transition-colors text-xl" />
                    </button>
                  </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-3 sm:p-4">
                  <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Observaciones</label>
                  <textarea v-model="form.observaciones" rows="2" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white resize-none" placeholder="Detalles adicionales..."></textarea>
                </div>

                <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-xl border border-blue-100 dark:border-blue-800/30">
                  <div class="flex gap-2 items-start">
                    <Icon icon="mdi:information-outline" class="text-blue-500 text-base shrink-0 mt-0.5" />
                    <p class="text-[9px] text-blue-700 dark:text-blue-300 font-bold leading-tight">
                      <strong>💡 Agrega items para cobrar.</strong><br>
                      Usa el botón 🛒 de la tabla para agregar servicios/productos. El costo base es solo referencia — <strong>solo se cobran los items agregados</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="px-4 sm:px-6 py-3 sm:py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0">
            <button @click="showFormModal = false" class="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all text-[10px] sm:text-xs">
              CANCELAR
            </button>
            <button @click="guardarServicio" :disabled="saving || !form.idCliente || !form.descripcion" class="w-full sm:w-auto px-6 sm:px-8 py-2 bg-indigo-600 text-white rounded-xl font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none flex items-center justify-center gap-2 text-[10px] sm:text-xs uppercase tracking-widest disabled:opacity-50">
              <Icon :icon="saving ? 'mdi:loading' : (editando ? 'mdi:content-save' : 'mdi:check-circle')" :class="saving ? 'animate-spin' : ''" />
              {{ saving ? 'Guardando...' : (editando ? 'Actualizar' : 'Confirmar') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE SELECCIÓN DE TÉCNICOS -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showTecnicoModal" class="fixed inset-0 z-[9999] overflow-hidden flex items-center justify-center p-2 sm:p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showTecnicoModal = false"></div>
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-zoomIn">
          <div class="p-4 sm:p-6 bg-gradient-to-r from-purple-600 to-violet-700">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-white/20 rounded-xl">
                  <Icon icon="mdi:account-wrench" class="text-2xl text-white" />
                </div>
                <div>
                  <h3 class="text-lg font-black text-white uppercase tracking-tighter">Seleccionar Técnico</h3>
                </div>
              </div>
              <button @click="showTecnicoModal = false" class="p-2 hover:bg-white/10 rounded-xl transition-all">
                <Icon icon="mdi:close" class="text-2xl text-white" />
              </button>
            </div>
          </div>
          <div class="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 bg-gray-50 dark:bg-gray-900/50">
            <div class="mb-4">
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                <input
                  v-model="tecnicoSearchModal"
                  type="text"
                  placeholder="Buscar técnico..."
                  class="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 focus:border-purple-500 rounded-xl outline-none text-sm font-medium dark:text-white"
                />
              </div>
            </div>
            <div v-if="tecnicosFiltradosModal.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div 
                v-for="t in tecnicosFiltradosModal" 
                :key="t.idTecnico"
                @click="seleccionarTecnicoModal(t)"
                class="p-4 bg-white dark:bg-gray-800 rounded-xl border-2 transition-all cursor-pointer group hover:shadow-lg"
                :class="tecnicoSeleccionadoTemp?.idTecnico === t.idTecnico ? 'border-purple-500 ring-2 ring-purple-500 ring-offset-2 dark:ring-offset-gray-800' : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'"
              >
                <div class="flex items-start gap-3">
                  <div class="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black flex-shrink-0" 
                       :class="tecnicoSeleccionadoTemp?.idTecnico === t.idTecnico ? 'bg-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'">
                    {{ (t.nombre || 'T').charAt(0) }}{{ (t.apellido || '').charAt(0) }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-bold text-gray-900 dark:text-white text-sm truncate">{{ t.nombre }} {{ t.apellido }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ t.email || 'Sin email' }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ t.telefono || 'Sin teléfono' }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12">
              <Icon icon="mdi:account-search" class="text-5xl text-gray-400 mx-auto mb-3" />
              <p class="text-gray-600 dark:text-gray-400 font-medium">No se encontraron técnicos</p>
            </div>
          </div>
          <div class="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex justify-end gap-2">
            <button @click="showTecnicoModal = false" class="px-6 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm hover:bg-gray-200 transition-all">Cancelar</button>
            <button @click="confirmarSeleccionTecnico" :disabled="!tecnicoSeleccionadoTemp" class="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-violet-700 text-white rounded-xl font-bold text-sm hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-2">
              <Icon icon="mdi:check" /> Confirmar
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE DETALLES DEL SERVICIO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showDetailModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9997]" @click.self="showDetailModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col animate-zoomIn">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon icon="mdi:receipt-text" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-black text-white">Detalles del Servicio</h3>
                <p class="text-[8px] sm:text-[10px] text-blue-100">#{{ detalleServicio?.numeroServicio }}</p>
              </div>
            </div>
            <button @click="showDetailModal = false" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>

          <div class="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex-1">
            <div v-if="detalleServicio" class="space-y-4">
              <div class="grid grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <p class="text-[9px] font-black text-gray-400 uppercase tracking-wider">Cliente</p>
                  <p class="text-sm font-black text-gray-800 dark:text-white mt-0.5">{{ detalleServicio.nombreCliente || 'N/A' }}</p>
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <p class="text-[9px] font-black text-gray-400 uppercase tracking-wider">Técnico</p>
                  <p class="text-sm font-black text-gray-800 dark:text-white mt-0.5">{{ detalleServicio.nombreTecnico || 'Sin asignar' }}</p>
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <p class="text-[9px] font-black text-gray-400 uppercase tracking-wider">Estado</p>
                  <span :class="getEstadoStyle(detalleServicio.estado)" class="inline-block mt-0.5 px-2 py-0.5 rounded-lg text-[10px] font-black uppercase">{{ detalleServicio.estado }}</span>
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <p class="text-[9px] font-black text-gray-400 uppercase tracking-wider">Fecha</p>
                  <p class="text-sm font-black text-gray-800 dark:text-white mt-0.5">{{ formatearFecha(detalleServicio.fechaProgramada) }}</p>
                </div>
              </div>

              <div class="bg-white dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-1">Descripción</p>
                <p class="text-sm text-gray-700 dark:text-gray-300">{{ detalleServicio.descripcion || 'Sin descripción' }}</p>
              </div>

              <div class="bg-white dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-1">Dirección</p>
                <p class="text-sm text-gray-700 dark:text-gray-300">{{ detalleServicio.direccion || 'Sin dirección' }}</p>
              </div>

              <div class="border-t-2 border-gray-200 dark:border-gray-700 pt-4">
                <h4 class="text-xs font-black text-gray-800 dark:text-white uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Icon icon="mdi:package-variant-closed" class="text-purple-500" />
                  Servicios y Productos usados
                  <span class="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-[10px]">
                    {{ detalleServicio.detalles?.length || 0 }}
                  </span>
                </h4>

                <div v-if="detalleServicio.detalles && detalleServicio.detalles.length > 0" class="space-y-2">
                  <div 
                    v-for="d in detalleServicio.detalles" 
                    :key="d.idServicioExternoDetalle" 
                    class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700"
                  >
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                      <div :class="d.tipoItem === 'servicio' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'" class="p-2 rounded-lg shrink-0">
                        <Icon :icon="d.tipoItem === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-base" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-bold text-gray-800 dark:text-white truncate">
                          {{ resolverNombreDetalle(d) }}
                        </p>
                        <p class="text-[10px] text-gray-400 font-bold uppercase">
                          {{ d.cantidad }}x {{ d.tipoItem }} · ${{ (d.precioUnitarioUsd || 0).toFixed(2) }} c/u
                        </p>
                      </div>
                    </div>
                    <p class="text-sm font-black text-emerald-600 dark:text-emerald-400 shrink-0">
                      ${{ (d.totalUsd || 0).toFixed(2) }}
                    </p>
                  </div>
                </div>

                <div v-else class="text-center py-6 bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                  <Icon icon="mdi:cart-outline" class="text-3xl text-gray-400 mb-2" />
                  <p class="text-[11px] text-gray-500 font-bold">Sin servicios ni productos agregados</p>
                </div>
              </div>

              <div v-if="detalleServicio.subtotalUsd > 0" class="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800">
                <div class="flex justify-between items-center text-sm">
                  <span class="text-gray-600 dark:text-gray-400 font-bold">Subtotal:</span>
                  <span class="font-black text-gray-800 dark:text-white">${{ (detalleServicio.subtotalUsd || 0).toFixed(2) }}</span>
                </div>
                <div v-if="detalleServicio.ivaUsd > 0" class="flex justify-between items-center text-sm mt-1">
                  <span class="text-gray-600 dark:text-gray-400 font-bold">IVA (16%):</span>
                  <span class="font-black text-orange-600">${{ (detalleServicio.ivaUsd || 0).toFixed(2) }}</span>
                </div>
                <div class="flex justify-between items-center pt-2 mt-2 border-t-2 border-emerald-300 dark:border-emerald-700">
                  <span class="text-sm font-black text-gray-800 dark:text-white">TOTAL:</span>
                  <span class="text-lg font-black text-emerald-700 dark:text-emerald-300">${{ (detalleServicio.totalUsd || 0).toFixed(2) }}</span>
                </div>
                <div class="mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-800 space-y-1">
                  <div v-if="detalleServicio.genera_iva" class="flex items-center gap-2 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
                    <Icon icon="mdi:check-circle" class="text-sm" />
                    Facturado con IVA — Registrado en libro
                  </div>
                  <div v-else class="flex items-center gap-2 text-[10px] font-bold text-amber-700 dark:text-amber-300">
                    <Icon icon="mdi:information-outline" class="text-sm" />
                    Sin IVA — No registrado en libro
                  </div>
                  <div v-if="detalleServicio.metodoPago" class="flex items-center gap-2 text-[10px] font-bold text-gray-600 dark:text-gray-400">
                    <Icon icon="mdi:credit-card" class="text-sm" />
                    Método: {{ detalleServicio.metodoPago }}
                  </div>
                  <div v-if="detalleServicio.estadoCredito === 'pendiente'" class="flex items-center gap-2 text-[10px] font-bold text-purple-700 dark:text-purple-300">
                    <Icon icon="mdi:clock-outline" class="text-sm" />
                    Crédito / Fiado
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2 flex-shrink-0">
            <button @click="showDetailModal = false" class="px-6 py-2 border rounded-lg font-black text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">
              Cerrar
            </button>
            <button 
              v-if="detalleServicio && detalleServicio.estado === 'EN PROCESO'"
              @click="abrirModalItems(detalleServicio); showDetailModal = false"
              class="px-6 py-2 bg-purple-600 text-white rounded-lg font-black text-xs hover:bg-purple-700 transition-all flex items-center gap-2"
            >
              <Icon icon="mdi:cart-plus" /> Gestionar Items
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE PAGO / COBRAR -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showPaymentModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]" @click.self="showPaymentModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col animate-zoomIn">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <Icon icon="mdi:cash-check" class="text-white text-lg sm:text-xl" />
              <div>
                <h3 class="text-sm sm:text-base font-black text-white">Cobrar Servicio</h3>
                <p class="text-[8px] sm:text-[10px] text-emerald-100">#{{ itemPago?.numeroServicio }}</p>
              </div>
            </div>
            <button @click="showPaymentModal = false" class="text-white/80 hover:text-white">
              <Icon icon="mdi:close" class="text-xl sm:text-2xl" />
            </button>
          </div>

          <div class="p-4 sm:p-5 overflow-y-auto flex-1 custom-scrollbar">
            <!-- Cliente -->
            <div class="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg mb-4">
              <div class="flex justify-between items-center">
                <div class="flex-1 min-w-0 pr-3">
                  <p class="text-[9px] text-gray-500 dark:text-gray-400 font-bold">Cliente</p>
                  <p class="text-sm font-black text-gray-800 dark:text-white truncate">{{ itemPago?.nombreCliente }}</p>
                </div>
                <div class="text-right flex-shrink-0">
                  <p class="text-[9px] text-gray-500 dark:text-gray-400 font-bold">Total a Cobrar</p>
                  <p class="text-base font-black text-emerald-600">${{ totalAPagarConItems.toFixed(2) }}</p>
                </div>
              </div>
            </div>

            <!-- Items agregados -->
            <div v-if="itemsPago.length > 0" class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 sm:p-4 mb-4 border-2 border-purple-200 dark:border-purple-800">
              <h4 class="text-[10px] sm:text-xs font-black text-purple-700 dark:text-purple-300 uppercase flex items-center gap-2 mb-2">
                <Icon icon="mdi:cart" class="text-sm" /> Items a cobrar ({{ itemsPago.length }})
              </h4>
              <div class="space-y-1.5">
                <div v-for="(it, idx) in itemsPago" :key="it.uuid" class="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg group">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <div :class="it.tipo === 'servicio' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'" class="p-1 rounded-lg shrink-0">
                      <Icon :icon="it.tipo === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-xs" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-[10px] font-bold text-gray-800 dark:text-white truncate">{{ it.nombre }}</p>
                      <p class="text-[8px] text-gray-400 font-black uppercase tracking-widest">{{ it.cantidad }}x {{ it.tipo }} · ${{ (it.precio_unitario || 0).toFixed(2) }} c/u</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-black text-emerald-600 dark:text-emerald-400">${{ (it.precio_total || it.precio || 0).toFixed(2) }}</span>
                    <button 
                      @click="eliminarItemExistente(idx)" 
                      class="p-1 text-rose-400 hover:text-white hover:bg-rose-500 rounded transition-all opacity-40 group-hover:opacity-100"
                      title="Eliminar este item"
                    >
                      <Icon icon="mdi:trash-can-outline" class="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex justify-between items-center pt-2 border-t border-purple-200 dark:border-purple-700 mt-2 text-[10px] font-bold">
                <span class="text-gray-500">Subtotal:</span>
                <span class="text-emerald-600">${{ totalItemsPago.toFixed(2) }}</span>
              </div>
            </div>

            <div v-else class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 mb-4 text-center border-2 border-dashed border-gray-200 dark:border-gray-700">
              <Icon icon="mdi:cart-outline" class="text-3xl text-gray-400 mb-2" />
              <p class="text-[11px] text-gray-500 font-bold">Sin items para cobrar</p>
              <p class="text-[9px] text-gray-400 mt-1">Usa el botón 🛒 de la tabla para agregar productos/servicios</p>
            </div>

            <!-- Toggle IVA -->
            <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-4 border-2 border-blue-200 dark:border-blue-800">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <Icon icon="mdi:file-document-check-outline" class="text-blue-600 text-lg" />
                  <div>
                    <p class="text-[11px] font-black text-blue-800 dark:text-blue-200">Registrar con IVA (16%)</p>
                    <p class="text-[9px] text-blue-600 dark:text-blue-400 font-bold">Se sumará al total y se registrará en el libro</p>
                  </div>
                </div>
                <button 
                  @click="pagoForm.generaIva = !pagoForm.generaIva" 
                  :class="['relative w-12 h-6 rounded-full transition-colors', pagoForm.generaIva ? 'bg-blue-600' : 'bg-gray-400']"
                >
                  <span :class="['absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm', pagoForm.generaIva ? 'right-0.5' : 'left-0.5']"></span>
                </button>
              </div>
            </div>

            <!-- Resumen TOTAL -->
            <div class="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-3 rounded-lg mb-4">
              <div class="flex justify-between items-center">
                <span class="text-[10px] font-black text-gray-600 dark:text-gray-400">Subtotal ({{ itemsPago.length }} items):</span>
                <span class="text-sm font-black text-gray-800 dark:text-white">${{ subtotalReal.toFixed(2) }}</span>
              </div>
              <div v-if="pagoForm.generaIva" class="flex justify-between items-center">
                <span class="text-[10px] font-black text-gray-600 dark:text-gray-400">IVA (16%):</span>
                <span class="text-sm font-black text-orange-600">${{ ivaCalculado.toFixed(2) }}</span>
              </div>
              <div v-else class="flex justify-between items-center">
                <span class="text-[10px] font-black text-gray-400 italic">Sin IVA</span>
                <span class="text-[10px] font-black text-gray-400 italic">—</span>
              </div>
              <div class="flex justify-between items-center pt-2 border-t-2 border-emerald-300 dark:border-emerald-700 mt-1">
                <span class="text-sm font-black text-gray-800 dark:text-white">TOTAL A PAGAR:</span>
                <span class="text-2xl font-black text-emerald-700 dark:text-emerald-300">${{ totalAPagarConItems.toFixed(2) }}</span>
              </div>
            </div>

            <!-- Método de pago -->
            <div>
              <label class="block text-[9px] sm:text-[10px] font-black text-gray-400 mb-1 uppercase">MÉTODO DE PAGO</label>
              
              <div v-if="loadingMetodosPago" class="text-center py-4">
                <Icon icon="mdi:loading" class="animate-spin text-2xl text-emerald-600" />
                <p class="text-xs text-gray-400 mt-1">Cargando métodos de pago...</p>
              </div>
              
              <div v-else class="grid grid-cols-3 sm:grid-cols-4 gap-1.5 sm:gap-2">
                <button 
                  v-for="met in metodosPagoList" 
                  :key="met.idMetodoPago" 
                  @click="seleccionarMetodo(met.idMetodoPago)" 
                  :class="[
                    'py-2 sm:py-3 rounded-lg text-[8px] sm:text-[10px] font-black transition-all flex flex-col items-center gap-1 border-2',
                    pagoForm.idMetodoPago === met.idMetodoPago 
                      ? (met.esCredito ? 'bg-purple-600 text-white border-purple-700 shadow-md' : 'bg-emerald-600 text-white border-emerald-700 shadow-md')
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-transparent hover:border-gray-300'
                  ]"
                >
                  <Icon :icon="met.icon" class="text-lg sm:text-xl" />
                  <span class="leading-tight text-center px-0.5">{{ met.label }}</span>
                </button>
              </div>
            </div>

            <!-- Crédito -->
            <div v-if="esCreditoSeleccionado" class="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg mt-3 border-2 border-purple-300 dark:border-purple-700">
              <h4 class="text-[10px] font-black text-purple-700 dark:text-purple-300 uppercase mb-3 flex items-center gap-2">
                <Icon icon="mdi:calendar-clock" class="text-sm" /> Crédito / Fiado
              </h4>
              <div class="bg-amber-100 dark:bg-amber-900/30 p-2 rounded-lg mb-3">
                <p class="text-[9px] font-black text-amber-800 dark:text-amber-200 flex items-center gap-1">
                  <Icon icon="mdi:information" class="text-sm" />
                  El crédito NO se registra en caja (es fiado)
                </p>
              </div>
              <div class="space-y-3">
                <div>
                  <label class="block text-[9px] font-black text-gray-600 dark:text-gray-400 uppercase mb-1">📅 Fecha de Pago Acordada *</label>
                  <input v-model="pagoForm.fechaVencimiento" type="date" :min="new Date().toISOString().split('T')[0]" class="w-full px-3 py-2 border-2 border-purple-300 dark:border-purple-700 rounded-xl text-sm font-bold bg-white dark:bg-gray-800 outline-none" />
                </div>
                <div>
                  <label class="block text-[9px] font-black text-gray-600 dark:text-gray-400 uppercase mb-1">📝 Observaciones</label>
                  <textarea v-model="pagoForm.observacionesCredito" rows="2" class="w-full px-3 py-2 border-2 border-purple-300 dark:border-purple-700 rounded-xl text-sm bg-white dark:bg-gray-800 outline-none resize-none"></textarea>
                </div>
              </div>
            </div>

            <!-- Pago normal -->
            <div v-else class="bg-amber-50 dark:bg-amber-900/20 p-2.5 rounded-lg mt-3">
              <div class="flex items-center justify-between mb-1">
                <div class="flex items-center gap-1">
                  <Icon icon="mdi:cash-register" class="text-amber-600 text-sm" />
                  <span class="text-[10px] font-black">Registrar en Caja</span>
                </div>
                <button @click="pagoForm.registrarEnCaja = !pagoForm.registrarEnCaja" :class="['relative w-10 h-5 rounded-full transition-colors', pagoForm.registrarEnCaja ? 'bg-emerald-600' : 'bg-gray-400']">
                  <span :class="['absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform', pagoForm.registrarEnCaja ? 'right-0.5' : 'left-0.5']"></span>
                </button>
              </div>
            </div>

            <template v-if="!esCreditoSeleccionado && pagoForm.registrarEnCaja">
              <div class="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3 space-y-2">
                <div>
                  <label class="block text-[9px] font-black text-gray-400 mb-1 uppercase">MONEDA</label>
                  <div class="flex gap-2">
                    <button @click="pagoForm.moneda = 'USD'" :class="['flex-1 py-1.5 rounded-lg text-[10px] font-black', pagoForm.moneda === 'USD' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700']">USD $</button>
                    <button @click="pagoForm.moneda = 'VES'" :class="['flex-1 py-1.5 rounded-lg text-[10px] font-black', pagoForm.moneda === 'VES' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700']">VES Bs.</button>
                  </div>
                </div>
                <div v-if="pagoForm.moneda === 'VES'">
                  <label class="block text-[9px] font-black text-gray-400 mb-1 uppercase">TASA (Bs/USD)</label>
                  <input v-model="pagoForm.tasaCambio" type="number" step="0.01" placeholder="40.00" class="w-full px-3 py-1.5 border rounded-lg text-xs font-bold dark:text-white dark:bg-gray-800" />
                </div>
                <div v-if="!esEfectivoSeleccionado">
                  <label class="block text-[9px] font-black text-gray-400 mb-1 uppercase">REFERENCIA</label>
                  <input v-model="pagoForm.referencia" placeholder="Número de referencia" class="w-full px-3 py-1.5 border rounded-lg text-xs font-bold dark:text-white dark:bg-gray-800" />
                </div>
              </div>
            </template>

            <!-- 🏦 SECCIÓN DE TRAZABILIDAD DE PAGOS (CUENTA BANCARIA) -->
            <div v-if="!esCreditoSeleccionado" class="bg-gradient-to-br from-violet-50/70 to-indigo-50/70 dark:from-violet-950/20 dark:to-indigo-950/20 rounded-xl p-3 mt-3 border-2 border-violet-200 dark:border-violet-800/60 space-y-2.5">
              <div class="flex items-center gap-2">
                <div class="w-6 h-6 bg-violet-600 text-white rounded-lg flex items-center justify-center shadow-sm">
                  <Icon icon="mdi:bank-check" class="text-sm" />
                </div>
                <div>
                  <h4 class="text-[10px] font-black text-violet-900 dark:text-violet-200 uppercase tracking-wider flex items-center gap-1.5">
                    Trazabilidad de Pagos
                    <span class="text-[8px] px-1.5 py-0.2 bg-violet-100 dark:bg-violet-900/60 text-violet-700 dark:text-violet-300 rounded-full font-black">
                      ¿Dónde cayó la plata?
                    </span>
                  </h4>
                  <p class="text-[8px] text-gray-500 dark:text-gray-400 font-medium">Asocia este cobro a una cuenta bancaria de la empresa</p>
                </div>
              </div>

              <!-- Selector Cuenta Destino -->
              <div>
                <label class="block text-[8px] sm:text-[9px] font-black text-violet-800 dark:text-violet-300 uppercase tracking-wider mb-1">
                  Cuenta Bancaria Destino <span v-if="esMetodoBancario" class="text-red-500">*</span>
                </label>
                <select
                  v-model="pagoForm.idCuentaBancaria"
                  class="w-full px-2.5 py-1.5 bg-white dark:bg-gray-800 border-2 border-violet-200 dark:border-violet-700 focus:border-violet-500 rounded-lg outline-none font-black text-xs text-gray-900 dark:text-white"
                >
                  <option :value="null">Sin cuenta bancaria (Efectivo en mano / No bancario)</option>
                  <option v-for="c in cuentasBancarias" :key="c.idCuentaBancaria" :value="c.idCuentaBancaria">
                    {{ c.bancoNombre }} — {{ c.titularNombreCompleto }} ({{ formatearNumeroCuenta(c.numeroCuenta) }})
                  </option>
                </select>
                <p v-if="cuentasBancarias.length === 0" class="mt-1 text-[8px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <Icon icon="mdi:alert" /> No hay cuentas bancarias activas registradas.
                </p>
              </div>

              <!-- Preview de Cuenta Bancaria seleccionada -->
              <div v-if="cuentaBancariaSeleccionada" class="p-2 bg-white dark:bg-gray-800/80 rounded-lg border border-violet-200 dark:border-violet-700/60 text-[9px] space-y-1 shadow-sm">
                <div class="flex items-center justify-between">
                  <span class="font-black text-gray-900 dark:text-white flex items-center gap-1">
                    <Icon icon="mdi:bank" class="text-violet-600 text-xs" />
                    {{ cuentaBancariaSeleccionada.bancoNombre }} ({{ cuentaBancariaSeleccionada.bancoCodigo }})
                  </span>
                  <span class="font-mono text-violet-700 dark:text-violet-300 font-bold">
                    {{ formatearNumeroCuenta(cuentaBancariaSeleccionada.numeroCuenta) }}
                  </span>
                </div>
                <div class="text-[8px] text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-2">
                  <span><b class="text-gray-700 dark:text-gray-300">Titular:</b> {{ cuentaBancariaSeleccionada.titularNombreCompleto }}</span>
                  <span v-if="cuentaBancariaSeleccionada.titularDocumento"><b class="text-gray-700 dark:text-gray-300">Doc:</b> {{ cuentaBancariaSeleccionada.titularDocumento }}</span>
                  <span v-if="cuentaBancariaSeleccionada.titularTelefono"><b class="text-gray-700 dark:text-gray-300">Tel:</b> {{ cuentaBancariaSeleccionada.titularTelefono }}</span>
                </div>
              </div>

              <!-- DATOS PAGO MÓVIL EMISOR -->
              <div v-if="esMetodoPagoMovil" class="bg-indigo-50/80 dark:bg-indigo-900/30 rounded-lg p-2 border border-indigo-200 dark:border-indigo-800 space-y-1.5">
                <div class="flex items-center gap-1 text-indigo-700 dark:text-indigo-300 font-black text-[9px] uppercase">
                  <Icon icon="mdi:cellphone" class="text-xs" />
                  <span>Datos del Pago Móvil (Cliente que transfiere)</span>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[9px]">
                  <div>
                    <label class="block text-[8px] font-black text-gray-500 dark:text-gray-400 uppercase mb-0.5">Banco Origen / Emisor</label>
                    <select
                      v-model="pagoForm.pmBancoEmisor"
                      class="w-full px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs font-bold dark:text-white outline-none"
                    >
                      <option value="">Seleccionar banco...</option>
                      <option v-for="b in bancosVenezuela" :key="b" :value="b">{{ b }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-[8px] font-black text-gray-500 dark:text-gray-400 uppercase mb-0.5">Cédula / Documento Emisor</label>
                    <input
                      v-model="pagoForm.pmDocumentoEmisor"
                      type="text"
                      placeholder="V-12345678"
                      class="w-full px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs font-bold dark:text-white outline-none"
                    />
                  </div>
                  <div>
                    <label class="block text-[8px] font-black text-gray-500 dark:text-gray-400 uppercase mb-0.5">Teléfono Emisor</label>
                    <input
                      v-model="pagoForm.pmTelefonoEmisor"
                      type="text"
                      placeholder="0414-1234567"
                      class="w-full px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs font-bold dark:text-white outline-none"
                    />
                  </div>
                  <div>
                    <label class="block text-[8px] font-black text-gray-500 dark:text-gray-400 uppercase mb-0.5">Código de Operación / Ref</label>
                    <input
                      v-model="pagoForm.pmCodigoOperacion"
                      type="text"
                      placeholder="Ej: 123456"
                      class="w-full px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs font-bold dark:text-white outline-none"
                    />
                  </div>
                </div>
              </div>

              <!-- DATOS TRANSFERENCIA (BANCO ORIGEN) -->
              <div v-else-if="esMetodoTransferencia" class="bg-blue-50/80 dark:bg-blue-900/30 rounded-lg p-2 border border-blue-200 dark:border-blue-800 space-y-1">
                <label class="block text-[8px] font-black text-gray-500 dark:text-gray-400 uppercase mb-0.5">Banco Origen de la Transferencia</label>
                <select
                  v-model="pagoForm.pmBancoEmisor"
                  class="w-full px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs font-bold dark:text-white outline-none"
                >
                  <option value="">Seleccionar banco emisor...</option>
                  <option v-for="b in bancosVenezuela" :key="b" :value="b">{{ b }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="px-4 sm:px-5 py-3 bg-gray-50 dark:bg-gray-900/50 flex flex-col sm:flex-row gap-2 flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
            <button @click="showPaymentModal = false" class="w-full sm:flex-1 py-2 border rounded-lg font-black text-[10px] sm:text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button 
              @click="finalizarConPago" 
              :disabled="paying || (esCreditoSeleccionado && !pagoForm.fechaVencimiento) || !pagoForm.idMetodoPago" 
              class="w-full sm:flex-1 py-2 bg-emerald-600 text-white rounded-lg font-black text-[10px] sm:text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Icon v-if="paying" icon="mdi:loading" class="animate-spin" />
              <Icon v-else icon="mdi:check-circle" />
              {{ paying ? 'Procesando...' : esCreditoSeleccionado ? 'Crear Crédito' : 'Finalizar Servicio' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CATÁLOGO (Items) -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showCatalogModal" class="fixed inset-0 z-[10001] overflow-hidden flex items-center justify-center p-2 sm:p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showCatalogModal = false"></div>
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-fadeIn">
          <div class="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div class="flex justify-between items-center">
              <h3 class="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <div class="p-2 rounded-lg" :class="catalogType === 'servicio' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'">
                  <Icon :icon="catalogType === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-xl" />
                </div>
                Gestionar Items del Servicio
              </h3>
              <button @click="showCatalogModal = false" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl">
                <Icon icon="mdi:close" class="text-2xl text-gray-500" />
              </button>
            </div>

            <!-- Items ya agregados -->
            <div v-if="itemsPago.length > 0" class="mt-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 border-2 border-purple-200 dark:border-purple-800">
              <h4 class="text-[10px] font-black text-purple-700 dark:text-purple-300 uppercase flex items-center gap-2 mb-2">
                <Icon icon="mdi:cart" class="text-sm" /> Items actuales ({{ itemsPago.length }})
              </h4>
              <div class="space-y-1.5 max-h-40 overflow-y-auto custom-scrollbar">
                <div v-for="(it, idx) in itemsPago" :key="it.uuid" class="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg group">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <div :class="it.tipo === 'servicio' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'" class="p-1 rounded-lg shrink-0">
                      <Icon :icon="it.tipo === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-xs" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-[10px] font-bold text-gray-800 dark:text-white truncate">{{ it.nombre }}</p>
                      <p class="text-[8px] text-gray-400 font-black uppercase tracking-widest">{{ it.cantidad }}x · ${{ (it.precio_total || 0).toFixed(2) }}</p>
                    </div>
                  </div>
                  <button 
                    @click="eliminarItemExistente(idx)" 
                    class="p-1 text-rose-400 hover:text-white hover:bg-rose-500 rounded transition-all opacity-40 group-hover:opacity-100"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:trash-can-outline" class="text-sm" />
                  </button>
                </div>
              </div>
            </div>
            
            <div class="mt-3 flex gap-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl">
              <button @click="catalogType = 'servicio'; catalogSearch = ''" :class="['flex-1 py-2 rounded-lg text-xs font-black transition-all', catalogType === 'servicio' ? 'bg-white dark:bg-gray-800 text-green-600 shadow-sm' : 'text-gray-500']">
                <Icon icon="mdi:wrench" class="inline mr-1" /> Servicios
              </button>
              <button @click="catalogType = 'producto'; catalogSearch = ''" :class="['flex-1 py-2 rounded-lg text-xs font-black transition-all', catalogType === 'producto' ? 'bg-white dark:bg-gray-800 text-blue-600 shadow-sm' : 'text-gray-500']">
                <Icon icon="mdi:package-variant" class="inline mr-1" /> Productos
              </button>
            </div>

            <div class="mt-3">
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input v-model="catalogSearch" type="text" placeholder="Buscar por nombre..." class="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 font-medium outline-none" />
              </div>
            </div>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto flex-1 custom-scrollbar">
            <div v-if="filteredCatalogItems.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div v-for="item in filteredCatalogItems" :key="item.id" @click="seleccionarItemCatalogo(item)" class="p-4 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] bg-white dark:bg-gray-800" :class="catalogType === 'servicio' ? 'hover:border-green-500' : 'hover:border-blue-500'">
                <div class="flex gap-3">
                  <div class="p-3 rounded-lg h-fit" :class="catalogType === 'servicio' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'">
                    <Icon :icon="catalogType === 'servicio' ? 'mdi:wrench' : 'mdi:package'" class="text-2xl" :class="catalogType === 'servicio' ? 'text-green-600' : 'text-blue-600'" />
                  </div>
                  <div class="flex-1">
                    <h4 class="font-semibold text-gray-900 dark:text-white">{{ item.nombre }}</h4>
                    <div class="mt-2 flex justify-between items-center">
                      <span class="text-sm text-gray-600 dark:text-gray-400">Precio USD:</span>
                      <span class="text-lg font-bold" :class="catalogType === 'servicio' ? 'text-green-600' : 'text-blue-600'">${{ (catalogType === 'servicio' ? (item.precioUsd || 0) : (item.precio_venta_usd || 0)).toFixed(2) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12">
              <Icon icon="mdi:magnify-close" class="text-5xl text-gray-400 mx-auto mb-3" />
              <p class="text-gray-600 dark:text-gray-400">No se encontraron resultados</p>
            </div>
          </div>
          <div class="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div class="flex justify-between items-center">
              <p class="text-sm text-gray-600 dark:text-gray-400">Selecciona para agregar al servicio</p>
              <button @click="showCatalogModal = false" class="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl hover:bg-gray-300 transition-all font-medium">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CONFIG ITEM -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showItemConfigModal" class="fixed inset-0 z-[10000] overflow-hidden flex items-center justify-center p-2 sm:p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeItemConfigModal"></div>
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden animate-fadeIn">
          <div class="p-5 bg-gradient-to-r" :class="itemConfigTipo === 'servicio' ? 'from-green-600 to-emerald-700' : 'from-blue-600 to-indigo-700'">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-white/20 rounded-xl">
                  <Icon :icon="itemConfigTipo === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-2xl text-white" />
                </div>
                <div>
                  <h3 class="text-lg font-black text-white uppercase">Configurar {{ itemConfigTipo }}</h3>
                </div>
              </div>
              <button @click="closeItemConfigModal" class="p-2 hover:bg-white/10 rounded-xl">
                <Icon icon="mdi:close" class="text-2xl text-white" />
              </button>
            </div>
          </div>
          <div class="p-5 space-y-4 bg-gray-50/50 dark:bg-gray-900/50">
            <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <p class="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-1">Item</p>
              <p class="font-bold text-gray-800 dark:text-white text-sm">{{ itemConfigSeleccionado?.nombre || '' }}</p>
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-500 uppercase mb-2">PRECIO UNITARIO (USD)</label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 font-black text-green-600 text-sm">$</span>
                <input v-model.number="itemConfigPrecio" type="number" step="0.01" min="0.01" class="w-full pl-8 pr-3 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-lg font-black text-green-600 outline-none text-right" />
              </div>
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-500 uppercase mb-2">GARANTÍA (DÍAS)</label>
              <div class="flex gap-2">
                <input v-model.number="itemConfigGarantia" type="number" step="1" min="0" class="flex-1 px-3 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-sm font-bold dark:bg-gray-800 dark:text-white" />
                <div class="px-3 py-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-[9px] font-black text-gray-500 uppercase">{{ formatearGarantia(itemConfigGarantia || 0) }}</div>
              </div>
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-500 uppercase mb-2">CANTIDAD</label>
              <div class="flex items-center gap-3">
                <button @click="itemConfigCantidad = Math.max(1, itemConfigCantidad - 1)" class="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center font-black">
                  <Icon icon="mdi:minus" />
                </button>
                <input v-model.number="itemConfigCantidad" type="number" step="1" min="1" class="w-20 px-3 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-center text-lg font-black dark:bg-gray-800 dark:text-white" />
                <button @click="itemConfigCantidad = Math.min(999, itemConfigCantidad + 1)" class="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center font-black">
                  <Icon icon="mdi:plus" />
                </button>
                <div class="flex-1 text-right">
                  <p class="text-[8px] text-gray-400 uppercase">TOTAL</p>
                  <p class="text-xl font-black text-green-600">${{ (itemConfigPrecio * itemConfigCantidad).toFixed(2) }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="p-5 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
            <button @click="agregarItemPagoConConfiguracion" :disabled="savingItem" class="w-full py-3.5 rounded-xl flex items-center justify-center gap-2 font-black text-sm uppercase shadow-lg disabled:opacity-50" :class="itemConfigTipo === 'servicio' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'">
              <Icon :icon="savingItem ? 'mdi:loading' : 'mdi:check-circle'" :class="savingItem ? 'animate-spin' : ''" />
              {{ savingItem ? 'GUARDANDO...' : 'AGREGAR AL SERVICIO' }}
            </button>
            <button @click="closeItemConfigModal" class="w-full mt-2 py-2 text-[10px] font-black text-gray-400 uppercase">Cancelar</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CONFIRMACIÓN ELIMINAR -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[10002]" @click.self="showConfirmModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-zoomIn">
          <div class="p-5 sm:p-6 text-center">
            <div class="w-14 h-14 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon icon="mdi:alert-octagon" class="text-rose-500 text-3xl" />
            </div>
            <h3 class="text-base font-black text-gray-900 dark:text-white mb-2">¿Eliminar Servicio?</h3>
            <p class="text-xs text-gray-500 mb-3">Esta acción no se puede deshacer.</p>
            <p class="font-bold text-rose-600 mb-4">#{{ itemEliminar?.numeroServicio }}</p>
            <div class="flex gap-2">
              <button @click="showConfirmModal = false" class="flex-1 py-2 border rounded-xl font-black text-[10px] hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
              <button @click="ejecutarEliminacion" class="flex-1 py-2 bg-rose-600 text-white rounded-xl font-black text-[10px] hover:bg-rose-700">Eliminar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import api from '@/api/serviciosExternos.js';
import { obtenerClientesActivos, crearCliente } from '@/api/clientes.js';
import { obtenerTecnicos } from '@/api/tecnicos.js';
import { getCurrentUser } from '@/api/index.js';
import { obtenerServicios } from '@/api/servicios.js';
import { obtenerProductos } from '@/api/productos.js';
import { actualizarStockProducto } from '@/api/productos.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { obtenerMetodosPagoActivos } from '@/api/metodosPago.api.js';
import { obtenerCuentasPropias, obtenerBancosPagoMovil, crearPagoCuenta, formatearNumeroCuenta } from '@/api/bancos.js';

export default {
  name: 'ServicioExternoList',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      servicios: [],
      clientes: [],
      tecnicos: [],
      catServicios: [],
      catProductos: [],
      loading: false,
      saving: false,
      paying: false,
      savingItem: false,
      filtroBusqueda: '',
      filtroEstado: '',
      currentPage: 1,
      itemsPerPage: 5,
      maxVisiblePages: 10,
      showFormModal: false,
      showDetailModal: false,
      detalleServicio: null,
      editando: false,
      modoCliente: 'buscar',
      busquedaCliente: '',
      nuevoCliente: { nombre: '', apellido: '', documento: '', telefono: '', direccion: '' },
      form: {
        idServicioExterno: null,
        idCliente: null,
        idTecnico: null,
        descripcion: '',
        direccion: '',
        ubicacionGps: '',
        fechaProgramada: null,
        fechaFinalizacion: null,
        costo: 0,
        prioridad: 'MEDIA',
        estado: 'PENDIENTE',
        observaciones: ''
      },
      showTecnicoModal: false,
      tecnicoSearchModal: '',
      tecnicoSeleccionadoTemp: null,
      showPaymentModal: false,
      itemPago: null,
      itemsPago: [],
      metodosPagoDB: [],
      loadingMetodosPago: false,
      cuentasBancarias: [],
      bancosVenezuela: [],
      tasaActualVenta: 1,
      pagoForm: {
        idMetodoPago: null,
        metodoObj: null,
        referencia: '',
        monto: 0,
        moneda: 'USD',
        tasaCambio: null,
        registrarEnCaja: true,
        fechaVencimiento: null,
        observacionesCredito: '',
        generaIva: false,
        idCuentaBancaria: null,
        pmBancoEmisor: '',
        pmTelefonoEmisor: '',
        pmDocumentoEmisor: '',
        pmCodigoOperacion: ''
      },
      showItemConfigModal: false,
      itemConfigTipo: 'servicio',
      itemConfigSeleccionado: null,
      itemConfigPrecio: 0,
      itemConfigGarantia: 0,
      itemConfigCantidad: 1,
      showCatalogModal: false,
      catalogType: 'servicio',
      catalogSearch: '',
      showConfirmModal: false,
      itemEliminar: null
    };
  },
  computed: {
    nombreTecnicoAsignado() {
      if (!this.form.idTecnico) return '';
      const t = this.tecnicos.find(x => x.idTecnico === this.form.idTecnico);
      return t ? `${t.nombre} ${t.apellido}` : '';
    },
    
    metodosPagoList() {
      if (!this.metodosPagoDB || this.metodosPagoDB.length === 0) {
        return [{ idMetodoPago: -1, nombre: 'Crédito Empresarial', label: 'Crédito Empresa', icon: 'mdi:domain', esCredito: true }];
      }
      return this.metodosPagoDB.map(m => ({
        idMetodoPago: m.idMetodoPago,
        label: m.nombre,
        nombre: m.nombre,
        tipo: m.tipo,
        icon: this.getIconoMetodoPago(m),
        esCredito: this.esMetodoCredito(m)
      }));
    },
    
    metodoSeleccionado() {
      if (!this.pagoForm.idMetodoPago) return null;
      return this.metodosPagoDB.find(m => m.idMetodoPago === this.pagoForm.idMetodoPago) || null;
    },
    
    esCreditoSeleccionado() {
      return this.esMetodoCredito(this.metodoSeleccionado);
    },
    
    esEfectivoSeleccionado() {
      const m = this.metodoSeleccionado;
      if (!m) return false;
      const t = (m.tipo || '').toLowerCase();
      const n = (m.nombre || '').toLowerCase();
      return t.includes('efectivo') || n.includes('efectivo');
    },
    
    cuentaBancariaSeleccionada() {
      if (!this.pagoForm.idCuentaBancaria) return null;
      return this.cuentasBancarias.find(c => c.idCuentaBancaria === this.pagoForm.idCuentaBancaria) || null;
    },

    esMetodoPagoMovil() {
      const m = this.metodoSeleccionado;
      if (!m) return false;
      const nom = (m.nombre || m.tipo || '').toLowerCase();
      return nom.includes('pago móvil') || nom.includes('pago movil') || nom.includes('cellphone') || nom === 'pago_movil';
    },

    esMetodoTransferencia() {
      const m = this.metodoSeleccionado;
      if (!m) return false;
      const nom = (m.nombre || m.tipo || '').toLowerCase();
      return nom.includes('transferencia');
    },

    esMetodoPuntoVenta() {
      const m = this.metodoSeleccionado;
      if (!m) return false;
      const nom = (m.nombre || m.tipo || '').toLowerCase();
      return nom.includes('punto') || nom.includes('tarjeta');
    },

    esMetodoBancario() {
      const nom = (this.metodoSeleccionado?.nombre || this.metodoSeleccionado?.tipo || '').toLowerCase();
      return this.esMetodoPagoMovil || this.esMetodoTransferencia || this.esMetodoPuntoVenta || nom.includes('zelle') || nom.includes('banco');
    },
    
    serviciosFiltrados() {
      let result = this.servicios;
      if (this.filtroEstado) result = result.filter(s => s.estado === this.filtroEstado);
      if (this.filtroBusqueda) {
        const q = this.filtroBusqueda.toLowerCase();
        result = result.filter(s => 
          (s.nombreCliente?.toLowerCase() || '').includes(q) ||
          (s.numeroServicio?.toString() || '').includes(q) ||
          (s.descripcion?.toLowerCase() || '').includes(q) ||
          (s.nombreTecnico?.toLowerCase() || '').includes(q)
        );
      }
      return result;
    },
    
    clientesFiltrados() {
      if (!this.busquedaCliente) return [];
      const q = this.busquedaCliente.toLowerCase();
      return this.clientes.filter(c => 
        (c.nombre?.toLowerCase() || '').includes(q) || 
        (c.apellido?.toLowerCase() || '').includes(q) ||
        (c.documento?.toLowerCase() || '').includes(q)
      ).slice(0, 5);
    },
    
    clienteActual() {
      return this.clientes.find(c => c.idCliente === this.form.idCliente);
    },
    
    totalPages() {
      return Math.ceil(this.serviciosFiltrados.length / this.itemsPerPage);
    },
    
    tecnicosFiltradosModal() {
      if (!this.tecnicoSearchModal) return this.tecnicos;
      const q = this.tecnicoSearchModal.toLowerCase().trim();
      return this.tecnicos.filter(t => 
        (t.nombre?.toLowerCase() || '').includes(q) || 
        (t.apellido?.toLowerCase() || '').includes(q) ||
        (t.email?.toLowerCase() || '').includes(q)
      );
    },
    
    paginatedServicios() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.serviciosFiltrados.slice(start, start + this.itemsPerPage);
    },
    
    displayedPages() {
      const total = this.totalPages;
      const max = this.maxVisiblePages;
      const current = this.currentPage;
      if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
      let start = Math.max(1, current - Math.floor(max / 2));
      let end = start + max - 1;
      if (end > total) { end = total; start = Math.max(1, end - max + 1); }
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    
    totalItemsPago() {
      return this.itemsPago.reduce((sum, it) => sum + (parseFloat(it.precio_total || it.precio || 0) || 0), 0);
    },
    
    subtotalBaseServicio() {
      return 0;
    },
    
    subtotalReal() {
      return this.totalItemsPago;
    },
    
    ivaCalculado() {
      if (!this.pagoForm.generaIva || this.esCreditoSeleccionado) return 0;
      return this.subtotalReal * 0.16;
    },
    
    totalAPagarConItems() {
      return this.subtotalReal + this.ivaCalculado;
    },
    
    filteredCatalogItems() {
      const termino = this.catalogSearch.toLowerCase().trim();
      let items = [];
      if (this.catalogType === 'servicio') {
        items = this.catServicios.map(s => ({ ...s, id: s.idServicio, precio: s.precioUsd }));
      } else {
        items = this.catProductos.map(p => ({ ...p, id: p.idProducto, precio: p.precio_venta_usd }));
      }
      if (!termino) return items.slice(0, 50);
      return items.filter(x => (x.nombre || '').toLowerCase().includes(termino)).slice(0, 50);
    }
  },
  async mounted() {
    await Promise.all([
      this.cargarCatalogos(),
      this.cargarServicios(),
      this.cargarMetodosPago(),
      this.cargarBancosYTasa()
    ]);
  },
  methods: {
    formatearNumeroCuenta,
    extraerCodigoBanco(valor) {
      if (!valor) return null;
      if (typeof valor === 'object') {
        return valor.pmBancoCodigo || valor.bancoCodigo || null;
      }
      const m = String(valor).match(/(\d{4})/);
      return m ? m[1] : null;
    },
    async cargarBancosYTasa() {
      try {
        const [cuentasRes, bancosRes, tasaRes] = await Promise.all([
          obtenerCuentasPropias().catch(() => ({ success: false })),
          obtenerBancosPagoMovil().catch(() => ({ success: false })),
          obtenerTasaVentaActual().catch(() => null)
        ]);

        if (cuentasRes && cuentasRes.success && Array.isArray(cuentasRes.data)) {
          this.cuentasBancarias = cuentasRes.data.filter(c => c.estado !== 'inactivo');
        }
        if (bancosRes && bancosRes.success && Array.isArray(bancosRes.data)) {
          this.bancosVenezuela = bancosRes.data.map(b => `${b.codigo} - ${b.nombre}`);
        }
        if (tasaRes && tasaRes.precio_usd_bs) {
          this.tasaActualVenta = tasaRes.precio_usd_bs;
        }
      } catch (e) {
        console.error('Error cargando bancos/tasa en ServicioExternoList:', e);
      }
    },
    goToFirstPage() { this.currentPage = 1; },
    goToLastPage() { this.currentPage = this.totalPages; },
    
    resolverNombreDetalle(d) {
      let nombre = d.nombreProducto 
        || d.nombreServicio 
        || d.nombreProductoServicio 
        || d.descripcionManual;
      
      if (nombre && nombre.trim() && nombre !== 'Item') return nombre;
      
      const tipo = (d.tipoItem || '').toLowerCase();
      
      if (tipo === 'servicio' && d.idServicio) {
        const s = this.catServicios.find(x => x.idServicio === d.idServicio);
        if (s?.nombre) return s.nombre;
      }
      
      if (tipo === 'producto' && d.idProducto) {
        const p = this.catProductos.find(x => x.idProducto === d.idProducto);
        if (p?.nombre) return p.nombre;
      }
      
      return 'Item';
    },
    
    obtenerFechaHoraLocal() {
      const ahora = new Date();
      const año = ahora.getFullYear();
      const mes = String(ahora.getMonth() + 1).padStart(2, '0');
      const dia = String(ahora.getDate()).padStart(2, '0');
      const horas = String(ahora.getHours()).padStart(2, '0');
      const minutos = String(ahora.getMinutes()).padStart(2, '0');
      return `${año}-${mes}-${dia}T${horas}:${minutos}`;
    },
    
    formatearGarantia(dias) {
      if (!dias || dias === 0) return 'Sin garantía';
      if (dias >= 365) return `${Math.floor(dias / 365)} año(s)`;
      if (dias >= 30) return `${Math.floor(dias / 30)} mes(es)`;
      return `${dias} día(s)`;
    },
    
    formatearFecha(fecha) {
      if (!fecha) return 'N/A';
      try { return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); } 
      catch { return fecha; }
    },
    
    formatearHora(f) { 
      if (!f) return '';
      try { return new Date(f).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }); } 
      catch { return ''; }
    },
    
    async cargarMetodosPago() {
      this.loadingMetodosPago = true;
      try {
        const res = await obtenerMetodosPagoActivos();
        let metodos = (res.success && res.data) ? res.data : [];
        
        const tieneCredito = metodos.some(m => {
          const n = (m.nombre || '').toLowerCase();
          const t = (m.tipo || '').toLowerCase();
          return n.includes('credito') || n.includes('crédito') || 
                 n.includes('empresa') || n.includes('empresarial') ||
                 t.includes('credito') || t.includes('aplicacion_credito');
        });
        
        if (!tieneCredito) {
          metodos.push({
            idMetodoPago: -1,
            nombre: 'Crédito Empresarial',
            descripcion: 'Fiado a empresas',
            tipo: 'aplicacion_credito',
            aceptaDolares: true,
            aceptaBolivares: true,
            aceptaUsdt: false
          });
        }
        
        this.metodosPagoDB = metodos;
      } catch (e) {
        console.error('❌ Error:', e);
        this.metodosPagoDB = [
          { idMetodoPago: 1, nombre: 'Efectivo', tipo: 'efectivo' },
          { idMetodoPago: -1, nombre: 'Crédito Empresarial', tipo: 'aplicacion_credito' }
        ];
      } finally {
        this.loadingMetodosPago = false;
      }
    },
    
    getIconoMetodoPago(metodo) {
      const tipo = (metodo.tipo || '').toLowerCase();
      const nombre = (metodo.nombre || '').toLowerCase();
      if (tipo.includes('efectivo') || nombre.includes('efectivo')) return 'mdi:cash';
      if (tipo.includes('transferencia') && nombre.includes('zelle')) return 'mdi:send';
      if (tipo.includes('transferencia')) return 'mdi:bank-transfer';
      if (tipo.includes('pago_movil') || nombre.includes('pago móvil')) return 'mdi:cellphone';
      if (tipo.includes('tarjeta') || nombre.includes('punto')) return 'mdi:credit-card';
      if (tipo.includes('cripto') || nombre.includes('usdt')) return 'mdi:currency-usd';
      if (tipo.includes('credito') || tipo.includes('aplicacion_credito')) {
        if (nombre.includes('cashea')) return 'mdi:lightning-bolt';
        if (nombre.includes('empresa')) return 'mdi:domain';
        return 'mdi:credit-card-clock';
      }
      return 'mdi:cash-multiple';
    },
    
    esMetodoCredito(metodo) {
      if (!metodo) return false;
      const tipo = (metodo.tipo || '').toLowerCase();
      const nombre = (metodo.nombre || '').toLowerCase();
      return tipo.includes('credito') 
        || tipo.includes('aplicacion_credito')
        || tipo.includes('crédito')
        || nombre.includes('credito') 
        || nombre.includes('crédito') 
        || nombre.includes('empresa')
        || nombre.includes('empresarial')
        || nombre.includes('fiado') 
        || nombre.includes('cashea');
    },
    
    async cargarServicios() {
      this.loading = true;
      try {
        const res = await api.obtenerServicios();
        if (res.success) this.servicios = res.data;
        else this.toast.error(res.message || "Error al cargar servicios");
      } catch (error) {
        this.toast.error("Error al cargar servicios");
      } finally { this.loading = false; }
    },
    
    async cargarCatalogos() {
      try {
        const [cRes, tRes, sRes, pRes] = await Promise.all([
          obtenerClientesActivos(), obtenerTecnicos(),
          obtenerServicios('activos'), obtenerProductos('activos')
        ]);
        if (cRes.success) this.clientes = cRes.data;
        if (tRes.success) this.tecnicos = tRes.data;
        if (sRes.success) this.catServicios = sRes.data;
        if (pRes.success) this.catProductos = pRes.data;
      } catch (error) { console.error("Error catalogos", error); }
    },
    
    abrirModalCreacion() {
      this.editando = false;
      this.modoCliente = 'buscar';
      this.busquedaCliente = '';
      const currentUser = getCurrentUser();
      let defaultIdTecnico = null;
      if (currentUser) {
        const userId = currentUser.idUsuario || currentUser.id;
        const tecnicoAsociado = this.tecnicos.find(t => parseInt(t.idUsuario) === parseInt(userId));
        if (tecnicoAsociado) defaultIdTecnico = tecnicoAsociado.idTecnico;
      }
      this.form = {
        idServicioExterno: null,
        idCliente: null,
        idTecnico: defaultIdTecnico,
        descripcion: '',
        direccion: '',
        ubicacionGps: '',
        fechaProgramada: this.obtenerFechaHoraLocal(),
        fechaFinalizacion: null,
        costo: 0,
        prioridad: 'MEDIA',
        estado: 'PENDIENTE',
        observaciones: ''
      };
      this.showFormModal = true;
    },
    
    abrirModalEdicion(item) {
      this.editando = true;
      const formatearFecha = (fecha) => {
        if (!fecha) return null;
        try { return new Date(fecha).toISOString().slice(0, 16); } catch { return null; }
      };
      this.form = { 
        ...item,
        idTecnico: item.idTecnico ? Number(item.idTecnico) : null,
        fechaProgramada: formatearFecha(item.fechaProgramada),
        fechaFinalizacion: formatearFecha(item.fechaFinalizacion)
      };
      this.showFormModal = true;
    },
    
    abrirModalDetalles(item) {
      this.detalleServicio = item;
      this.showDetailModal = true;
    },
    
    seleccionarCliente(c) {
      this.form.idCliente = c.idCliente;
      this.busquedaCliente = '';
      if (!this.form.direccion) this.form.direccion = c.direccion || '';
    },
    
    async crearYSeleccionarCliente() {
      try {
        const res = await crearCliente(this.nuevoCliente);
        if (res.success) {
          this.toast.success("Cliente creado");
          await this.cargarCatalogos();
          this.form.idCliente = res.data.idCliente;
          this.modoCliente = 'buscar';
          this.nuevoCliente = { nombre: '', apellido: '', documento: '', telefono: '', direccion: '' };
        }
      } catch (error) { this.toast.error("Error al crear cliente"); }
    },
    
    async guardarServicio() {
      if (!this.form.idCliente) { this.toast.error("Seleccione un cliente"); return; }
      if (!this.form.descripcion?.trim()) { this.toast.error("La descripción es requerida"); return; }
      this.saving = true;
      try {
        let estadoFinal = this.form.estado;
        if (estadoFinal === 'EN CAMINO') estadoFinal = 'EN PROCESO';
        
        const datosEnviar = {
          idCliente: this.form.idCliente,
          idTecnico: this.form.idTecnico,
          descripcion: this.form.descripcion,
          direccion: this.form.direccion,
          ubicacionGps: this.form.ubicacionGps || null,
          costo: 0,
          prioridad: this.form.prioridad,
          observaciones: this.form.observaciones || null,
          fechaProgramada: this.form.fechaProgramada ? new Date(this.form.fechaProgramada).toISOString() : null,
          fechaFinalizacion: this.form.fechaFinalizacion ? new Date(this.form.fechaFinalizacion).toISOString() : null,
          estado: estadoFinal
        };
        let res;
        if (this.editando) {
          res = await api.actualizarServicio(this.form.idServicioExterno, datosEnviar);
        } else {
          res = await api.crearServicio(datosEnviar);
        }
        if (res.success) {
          this.toast.success(this.editando ? "Servicio actualizado" : "Servicio creado");
          this.showFormModal = false;
          await this.cargarServicios();
        } else {
          this.toast.error(res.message || "Error al guardar");
        }
      } catch (error) {
        this.toast.error("Error al guardar");
      } finally { this.saving = false; }
    },
    
    abrirModalTecnicos() {
      this.tecnicoSearchModal = '';
      this.tecnicoSeleccionadoTemp = this.form.idTecnico ? this.tecnicos.find(t => t.idTecnico === this.form.idTecnico) : null;
      this.showTecnicoModal = true;
    },
    
    seleccionarTecnicoModal(tecnico) { this.tecnicoSeleccionadoTemp = tecnico; },
    
    confirmarSeleccionTecnico() {
      if (this.tecnicoSeleccionadoTemp) {
        this.form.idTecnico = this.tecnicoSeleccionadoTemp.idTecnico;
        this.showTecnicoModal = false;
        this.toast.success(`Técnico ${this.tecnicoSeleccionadoTemp.nombre} asignado`);
      }
    },
    
    abrirModalItems(item) {
      this.itemPago = item;
      this.itemsPago = (item.detalles || []).map(d => ({
        uuid: 'existente-' + d.idServicioExternoDetalle,
        id: d.idProducto || d.idServicio || d.idProductoServicios,
        idProducto: d.idProducto,
        idServicio: d.idServicio,
        idServicioExternoDetalle: d.idServicioExternoDetalle,
        tipo: (d.tipoItem || '').toLowerCase(),
        nombre: this.resolverNombreDetalle(d),
        precio_unitario: d.precioUnitarioUsd,
        precio_total: d.totalUsd,
        precio: d.totalUsd,
        cantidad: d.cantidad,
        garantia: 0,
        costo: d.costoUnitarioUsd || 0,
        _existente: true
      }));
      this.catalogType = 'servicio';
      this.catalogSearch = '';
      this.showCatalogModal = true;
    },
    
    abrirModalPago(item) {
      this.itemPago = item;
      this.itemsPago = (item.detalles || []).map(d => ({
        uuid: 'existente-' + d.idServicioExternoDetalle,
        id: d.idProducto || d.idServicio || d.idProductoServicios,
        idProducto: d.idProducto,
        idServicio: d.idServicio,
        idServicioExternoDetalle: d.idServicioExternoDetalle,
        tipo: (d.tipoItem || '').toLowerCase(),
        nombre: this.resolverNombreDetalle(d),
        precio_unitario: d.precioUnitarioUsd,
        precio_total: d.totalUsd,
        precio: d.totalUsd,
        cantidad: d.cantidad,
        garantia: 0,
        costo: d.costoUnitarioUsd || 0,
        _existente: true
      }));
      
      const fecha = new Date();
      fecha.setDate(fecha.getDate() + 30);
      
      const primerMetodo = this.metodosPagoDB[0];
      const primeraCuenta = this.cuentasBancarias.length > 0 ? this.cuentasBancarias[0].idCuentaBancaria : null;
      const clienteActual = this.clientes.find(c => c.idCliente === item.idCliente);
      const tel = item.telefono || item.telefonoCliente || clienteActual?.telefono || '';
      const doc = item.documento || item.cedula || clienteActual?.documento || '';

      this.pagoForm = {
        idMetodoPago: primerMetodo?.idMetodoPago || null,
        metodoObj: primerMetodo || null,
        referencia: '',
        monto: 0,
        moneda: 'USD',
        tasaCambio: this.tasaActualVenta || null,
        registrarEnCaja: true,
        fechaVencimiento: fecha.toISOString().split('T')[0],
        observacionesCredito: `Crédito por servicio externo #${this.itemPago?.numeroServicio || ''}`,
        generaIva: false,
        idCuentaBancaria: primeraCuenta,
        pmBancoEmisor: '',
        pmTelefonoEmisor: tel,
        pmDocumentoEmisor: doc,
        pmCodigoOperacion: ''
      };
      this.showPaymentModal = true;
    },
    
    seleccionarMetodo(idMetodoPago) {
      this.pagoForm.idMetodoPago = idMetodoPago;
      this.pagoForm.metodoObj = this.metodosPagoDB.find(m => m.idMetodoPago === idMetodoPago) || null;
      
      if (this.esCreditoSeleccionado) {
        this.pagoForm.registrarEnCaja = false;
        this.pagoForm.generaIva = false;
      } else {
        this.pagoForm.registrarEnCaja = true;
      }
    },
    
    seleccionarItemCatalogo(item) {
      this.itemConfigSeleccionado = item;
      if (this.catalogType === 'servicio') {
        this.itemConfigPrecio = item.precioUsd || 0;
        this.itemConfigGarantia = item.garantiaDias || 0;
      } else {
        this.itemConfigPrecio = item.precio_venta_usd || 0;
        this.itemConfigGarantia = (item.garantia_meses || 0) * 30;
      }
      this.itemConfigCantidad = 1;
      this.showCatalogModal = false;
      this.showItemConfigModal = true;
    },
    
    async agregarItemPagoConConfiguracion() {
      if (!this.itemConfigSeleccionado) return;
      if (this.itemConfigPrecio <= 0) { this.toast.warning("El precio debe ser mayor a 0"); return; }
      if (this.itemConfigCantidad < 1) { this.toast.warning("La cantidad debe ser al menos 1"); return; }
      
      this.savingItem = true;
      try {
        const detalleDTO = {
          tipoItem: this.catalogType,
          idProducto: this.catalogType === 'producto' ? this.itemConfigSeleccionado.id : null,
          idServicio: this.catalogType === 'servicio' ? this.itemConfigSeleccionado.id : null,
          cantidad: this.itemConfigCantidad,
          precioUnitarioUsd: this.itemConfigPrecio,
          observaciones: this.itemConfigGarantia > 0 ? `Garantía: ${this.formatearGarantia(this.itemConfigGarantia)}` : null
        };
        
        const res = await api.agregarDetalle(this.itemPago.idServicioExterno, detalleDTO);
        
        if (res.success) {
          this.toast.success(`✅ Agregado: $${(this.itemConfigPrecio * this.itemConfigCantidad).toFixed(2)}`);
          await this.cargarServicios();
          
          const actualizado = this.servicios.find(s => s.idServicioExterno === this.itemPago.idServicioExterno);
          if (actualizado) {
            this.itemPago = actualizado;
            this.itemsPago = (actualizado.detalles || []).map(d => ({
              uuid: 'existente-' + d.idServicioExternoDetalle,
              id: d.idProducto || d.idServicio || d.idProductoServicios,
              idProducto: d.idProducto,
              idServicio: d.idServicio,
              idServicioExternoDetalle: d.idServicioExternoDetalle,
              tipo: (d.tipoItem || '').toLowerCase(),
              nombre: this.resolverNombreDetalle(d),
              precio_unitario: d.precioUnitarioUsd,
              precio_total: d.totalUsd,
              precio: d.totalUsd,
              cantidad: d.cantidad,
              garantia: 0,
              costo: d.costoUnitarioUsd || 0,
              _existente: true
            }));
          }
          
          this.catalogSearch = '';
          this.showCatalogModal = true;
          this.closeItemConfigModal();
        } else {
          this.toast.error(res.message || "Error al agregar");
        }
      } catch (error) {
        console.error('❌ Error:', error);
        this.toast.error("Error al agregar el item");
      } finally {
        this.savingItem = false;
      }
    },
    
    async eliminarItemExistente(index) {
      const item = this.itemsPago[index];
      if (!item) return;
      
      if (!item.idServicioExternoDetalle || !item._existente) {
        this.itemsPago.splice(index, 1);
        this.toast.success('Item eliminado de la lista');
        return;
      }
      
      const ok = confirm(`¿Eliminar "${item.nombre}" del servicio?`);
      if (!ok) return;
      
      this.savingItem = true;
      try {
        const res = await api.eliminarDetalle(item.idServicioExternoDetalle);
        
        if (res.success) {
          this.itemsPago.splice(index, 1);
          this.toast.success('Item eliminado del servicio');
          await this.cargarServicios();
          
          const actualizado = this.servicios.find(s => s.idServicioExterno === this.itemPago?.idServicioExterno);
          if (actualizado) this.itemPago = actualizado;
        } else {
          this.toast.error(res.message || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error:', error);
        this.toast.error('Error al eliminar el item');
      } finally {
        this.savingItem = false;
      }
    },
    
    closeItemConfigModal() {
      this.showItemConfigModal = false;
      this.itemConfigSeleccionado = null;
      this.itemConfigPrecio = 0;
      this.itemConfigGarantia = 0;
      this.itemConfigCantidad = 1;
    },
    
    async obtenerTasaActual() {
      try { 
        const result = await obtenerTasaVentaActual(); 
        if (result.success && result.data) return result.data.precio_usd_bs || 52.00; 
        return 52.00; 
      } catch { return 52.00; }
    },
    
    async finalizarConPago() {
      const metodo = this.metodoSeleccionado;
      if (!metodo) { this.toast.error('Selecciona un método de pago'); return; }

      const total = this.totalAPagarConItems;
      
      if (this.esCreditoSeleccionado) {
        await this.crearCreditoParaServicio();
        return;
      }
      
      if (total <= 0) {
        const ok = confirm('El total es $0. ¿Marcar el servicio como CANCELADO?');
        if (!ok) return;
        
        this.paying = true;
        try {
          const res = await api.actualizarServicio(this.itemPago.idServicioExterno, {
            ...this.itemPago,
            estado: 'CANCELADO',
            observaciones: (this.itemPago.observaciones || '') + '\n📌 Cancelado: monto total en 0'
          });
          if (res.success) {
            this.toast.info('Servicio marcado como CANCELADO');
            this.showPaymentModal = false;
            await this.cargarServicios();
          }
        } catch (e) {
          this.toast.error("Error al cancelar: " + e.message);
        } finally {
          this.paying = false;
        }
        return;
      }

      this.paying = true;
      try {
        let montoUSD = 0, montoVES = 0;
        const totalFinal = this.totalAPagarConItems;
        
        if (this.pagoForm.registrarEnCaja && totalFinal > 0) {
          if (this.pagoForm.moneda === 'VES' && this.pagoForm.tasaCambio) {
            montoUSD = totalFinal / this.pagoForm.tasaCambio;
            montoVES = totalFinal;
          } else {
            montoUSD = totalFinal;
          }
        }

        const payload = {
          ...this.itemPago,
          estado: 'COMPLETADO',
          metodoPago: metodo.nombre,
          idMetodoPago: metodo.idMetodoPago > 0 ? metodo.idMetodoPago : null,
          referenciaPago: this.pagoForm.referencia || null,
          montoPagado: montoUSD || totalFinal,
          fechaPago: new Date().toISOString(),
          genera_iva: this.pagoForm.generaIva,
          ivaUsd: this.ivaCalculado,
          subtotalUsd: this.subtotalReal,
          totalUsd: totalFinal
        };

        const res = await api.actualizarServicio(this.itemPago.idServicioExterno, payload);

        if (res.success) {
          const productos = this.itemsPago.filter(i => i.tipo === 'producto' && i.id);
          for (const prod of productos) {
            try {
              const pRes = await this.obtenerProductoPorId(prod.id);
              if (pRes.success) {
                const nuevoStock = (pRes.data.stock || 0) - (prod.cantidad || 1);
                if (nuevoStock >= 0) await actualizarStockProducto(prod.id, nuevoStock);
              }
            } catch (e) { console.error('Error descontando stock:', e); }
          }

          if (this.pagoForm.registrarEnCaja && montoUSD > 0) {
            const pagoCaja = {
              monto: montoUSD,
              metodo: metodo.nombre,
              idMetodoPago: metodo.idMetodoPago > 0 ? metodo.idMetodoPago : null,
              referencia: this.pagoForm.referencia,
              monedaOriginal: this.pagoForm.moneda,
              montoOriginal: montoVES > 0 ? montoVES : null,
              tasaCambio: this.pagoForm.tasaCambio
            };
            await api.registrarPagoCaja(this.itemPago, pagoCaja);
            this.toast.success(`✅ Cobrado $${montoUSD.toFixed(2)} en caja`);
          } else {
            this.toast.success("Servicio finalizado");
          }

          // 🔥 TRAZABILIDAD DE PAGOS: Registrar en PagoCuentaBancaria
          if (this.pagoForm.idCuentaBancaria && (montoUSD > 0 || totalFinal > 0)) {
            try {
              const cuentaDestino = this.cuentasBancarias.find(c => c.idCuentaBancaria === this.pagoForm.idCuentaBancaria);
              const tasa = Number(this.pagoForm.tasaCambio) || this.tasaActualVenta || 1;
              const finalUsd = montoUSD > 0 ? montoUSD : totalFinal;
              const finalBs = montoVES > 0 ? montoVES : (finalUsd * tasa);

              let metodoFinal = 'otro';
              const metodoNom = (metodo.nombre || metodo.tipo || '').toLowerCase();
              if (metodoNom.includes('pago móvil') || metodoNom.includes('pago movil') || metodoNom.includes('cellphone') || metodoNom === 'pago_movil') {
                metodoFinal = 'pago_movil';
              } else if (metodoNom.includes('transferencia')) {
                metodoFinal = 'transferencia';
              } else if (metodoNom.includes('punto') || metodoNom.includes('tarjeta')) {
                metodoFinal = 'punto_venta';
              } else if (metodoNom.includes('zelle')) {
                metodoFinal = 'zelle';
              } else if (metodoNom.includes('efectivo')) {
                metodoFinal = 'efectivo';
              }

              const payloadCuenta = {
                tipoDocumentoPago: 'servicio_externo',
                idDocumentoPago: this.itemPago.idServicioExterno,
                idCuentaBancaria: this.pagoForm.idCuentaBancaria,
                montoUsd: finalUsd,
                montoBs: finalBs,
                tasaBsUsd: tasa,
                metodoPago: metodoFinal,
                referencia: this.pagoForm.referencia || 'S/R',
                fechaPago: new Date().toISOString(),
                pmBancoEmisorCodigo: this.esMetodoPagoMovil ? this.extraerCodigoBanco(this.pagoForm.pmBancoEmisor) : null,
                pmBancoReceptorCodigo: this.esMetodoPagoMovil ? (cuentaDestino?.pmBancoCodigo || cuentaDestino?.bancoCodigo || null) : null,
                pmTelefonoEmisor: this.esMetodoPagoMovil ? this.pagoForm.pmTelefonoEmisor : null,
                pmDocumentoEmisor: this.esMetodoPagoMovil ? this.pagoForm.pmDocumentoEmisor : null,
                pmCodigoOperacion: this.esMetodoPagoMovil ? (this.pagoForm.pmCodigoOperacion || this.pagoForm.referencia || null) : null,
                observaciones: `Pago Servicio Externo #${this.itemPago.numeroServicio || this.itemPago.idServicioExterno} - Cliente: ${this.itemPago.nombreCliente || 'N/A'}`
              };

              await crearPagoCuenta(payloadCuenta);
            } catch (errCuenta) {
              console.error('⚠️ No se pudo registrar la trazabilidad bancaria:', errCuenta);
              this.toast.warning('El servicio se finalizó, pero no se pudo asociar a la trazabilidad bancaria');
            }
          }

          this.showPaymentModal = false;
          await this.cargarServicios();
        } else {
          this.toast.error(res.message || "Error al procesar");
        }
      } catch (error) {
        console.error("Error:", error);
        this.toast.error("Error al procesar: " + error.message);
      } finally { this.paying = false; }
    },
    
    async crearCreditoParaServicio() {
      if (!this.itemPago?.idServicioExterno) { this.toast.error("Servicio no válido"); return; }
      if (!this.pagoForm.fechaVencimiento) { this.toast.error("Selecciona la fecha"); return; }

      const metodo = this.metodoSeleccionado;
      if (!metodo) { this.toast.error("Selecciona un método"); return; }

      this.paying = true;
      try {
        const total = this.totalAPagarConItems;
        if (total <= 0) { this.toast.warning("Monto 0"); return; }

        const nombreMetodo = (metodo.nombre || '').toLowerCase();
        let codigoTipoCredito = 'EMP-001';
        if (nombreMetodo.includes('cashea')) {
          codigoTipoCredito = 'CAS-001';
        }

        const creditoData = {
          idServicioExterno: this.itemPago.idServicioExterno,
          codigoTipoCredito: codigoTipoCredito,
          numeroCuotas: 1,
          fechaVencimientoBase: new Date(this.pagoForm.fechaVencimiento).toISOString(),
          observaciones: this.pagoForm.observacionesCredito || `Crédito ${codigoTipoCredito} por servicio externo #${this.itemPago.numeroServicio}`
        };

        const response = await api.crearCreditoServicioExterno(creditoData);

        if (response.success) {
          const idCredito = response.data?.idCreditoServicioExterno || response.data?.id;
          this.toast.success(`✅ Crédito de $${total.toFixed(2)} creado`);
          this.toast.info(`📅 Vence: ${this.formatearFecha(this.pagoForm.fechaVencimiento)}`);
          this.toast.info(`📋 Crédito NO se registra en caja (es fiado)`);

          await api.actualizarServicio(this.itemPago.idServicioExterno, {
            ...this.itemPago,
            estado: 'COMPLETADO',
            metodoPago: metodo.nombre,
            idMetodoPago: metodo.idMetodoPago > 0 ? metodo.idMetodoPago : null,
            idCreditoServicioExterno: idCredito,
            estadoCredito: 'pendiente',
            fechaPagoAcordada: this.pagoForm.fechaVencimiento,
            subtotalUsd: this.subtotalReal,
            totalUsd: total,
            observaciones: (this.itemPago.observaciones || '') + 
              `\n📌 Crédito ${codigoTipoCredito} #${idCredito} - $${total.toFixed(2)} - Vence: ${this.pagoForm.fechaVencimiento}`
          });

          this.showPaymentModal = false;
          await this.cargarServicios();
        } else {
          this.toast.error(response.message || "Error al crear el crédito");
        }
      } catch (error) {
        console.error("❌ Error:", error);
        this.toast.error("Error: " + error.message);
      } finally { this.paying = false; }
    },
    
    async obtenerProductoPorId(id) {
      try {
        const { apiFetch } = await import('@/api/index.js');
        const response = await apiFetch(`/Producto/${id}`);
        return { success: true, data: response };
      } catch (error) { return { success: false, error: error.message }; }
    },
    
    confirmarEliminar(item) { this.itemEliminar = item; this.showConfirmModal = true; },
    
    async ejecutarEliminacion() {
      if (!this.itemEliminar) return;
      try {
        const res = await api.eliminarServicio(this.itemEliminar.idServicioExterno);
        if (res.success) {
          this.toast.success("Eliminado");
          this.showConfirmModal = false;
          await this.cargarServicios();
        }
      } catch (error) { this.toast.error("Error al eliminar"); }
    },
    
    getEstadoStyle(est) {
      const estilos = {
        'PENDIENTE': 'bg-amber-500 text-white border-amber-600',
        'EN PROCESO': 'bg-indigo-500 text-white border-indigo-600',
        'COMPLETADO': 'bg-emerald-500 text-white border-emerald-600',
        'CANCELADO': 'bg-rose-500 text-white border-rose-600'
      };
      return estilos[est] || estilos['PENDIENTE'];
    },
    
    getPrioridadStyle(p) {
      const estilos = {
        'BAJA': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        'MEDIA': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        'ALTA': 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
        'URGENTE': 'bg-red-600 text-white'
      };
      return estilos[p] || estilos['MEDIA'];
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
</style>